import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogConfigModal } from "@/components/LogConfigModal";
import { ManualDataUploadModal } from "@/components/ManualDataUploadModal";
import { FormDataViewerModal } from "@/components/FormDataViewerModal";
import STLViewerModal from "@/components/STLViewerModal";
import ImageViewerModal from "@/components/ImageViewerModal";
import { 
  Plus, 
  ArrowRight, 
  Factory, 
  Settings, 
  Clock, 
  Users, 
  AlertTriangle,
  Trash2,
  Edit,
  Package,
  Car,
  GripVertical,
  Star,
  Database,
  Upload,
  FileText,
  BarChart3,
  Microscope,
  Shield,
  Wrench,
  Zap,
  LogOut,
  User,
  Building
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { useTenant } from "@/contexts/tenant-context";
import { WorkspaceService } from "@/services/workspace-service";
import { ProjectMemberService } from "@/services/project-member-service";
import { ProjectService } from '../services/project-service';
// import { DataConfigurationService } from '@/services/data-configuration-service';
import { getCurrentTenantSlug } from "@/utils/tenant-utils";
import { WorkspaceSettingsModal } from "@/components/WorkspaceSettingsModal";
import { ProjectMembersModal } from "@/components/ProjectMembersModal";
import { WorkspaceCreationModal } from "@/components/WorkspaceCreationModal";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS,
} from '@dnd-kit/utilities';
import { Header } from "@/components/Header";

interface Product {
  id: string;
  name: string;
  description: string | null;
  code: string | null;
  created_at: string;
  updated_at: string;
}

interface ManufacturingProcess {
  id: string;
  product_id: string;
  name: string;
  description: string | null;
  process_type: string;
  duration: number;
  workers: number;
  level: number;
  parent_id: string | null;
  sort_order: number;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
  children?: ManufacturingProcess[];
}

interface StationCardProps {
  station: ManufacturingProcess;
  onRemove: (id: string) => void;
  onAddSub: (parentId: string, parentLevel: number) => void;
  onToggleFavorite: (id: string) => void;
  onEdit: (station: ManufacturingProcess) => void;
  onConfigureLog: (station: ManufacturingProcess) => void;
  onManualUpload: (station: ManufacturingProcess) => void;
  onViewData: (station: ManufacturingProcess, formTitle: string, configId: string) => void;
  level: number;
  toast: (args: any) => void;
  isAdminMode: boolean;
  canUploadData: boolean;
  manualConfigs: any[];
  formSubmissions: any[];
  sampleDataCounts: Record<string, number>;
  dataConfigurationUsage: {
    current: number;
    limit: number;
    remaining: number;
    isUnlimited: boolean;
  } | null;
}

const SortableStationCard = ({ station, onRemove, onAddSub, onToggleFavorite, onEdit, onConfigureLog, onManualUpload, onViewData, level, toast, isAdminMode, canUploadData, manualConfigs, formSubmissions, sampleDataCounts, dataConfigurationUsage }: StationCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: station.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const stationTypes = {
    assembly: { color: 'bg-blue-100 text-blue-800', icon: Factory },
    quality: { color: 'bg-green-100 text-green-800', icon: Settings },
    packaging: { color: 'bg-purple-100 text-purple-800', icon: Package },
    inspection: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
    testing: { color: 'bg-red-100 text-red-800', icon: Settings }
  };
  
  const StationIcon = stationTypes[station.process_type as keyof typeof stationTypes]?.icon || Factory;
  const levelColors = {
    1: 'border-l-4 border-blue-500',
    2: 'border-l-4 border-green-500', 
    3: 'border-l-4 border-purple-500'
  };

  // Data links will be populated from configured log forms
  const stationConfigs = manualConfigs.filter(config => config.manufacturing_process_id === station.id);
  const dataLinks: { name: string; icon: any; count: number; configId: string }[] = stationConfigs.map(config => {
    let count = 0;
    
    if (config.data_type === 'data') {
      // For data configurations, count from sample_data table
      count = sampleDataCounts[config.id] || 0;
    } else {
      // For form configurations, count from form submissions
      count = formSubmissions.filter(submission => 
        submission.config_id === config.id && 
        submission.manufacturing_process_id === station.id
      ).length;
    }
    
    return {
      name: config.form_title || 'Untitled Form',
      icon: FileText,
      count: count,
      configId: config.id
    };
  });

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      className={`flex-1 ${levelColors[level as keyof typeof levelColors]} ${isDragging ? 'opacity-50' : ''}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div 
              {...attributes} 
              {...listeners}
              className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center cursor-grab hover:bg-primary/20 transition-colors"
            >
              <GripVertical className="w-4 h-4 text-primary" />
            </div>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <StationIcon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">{station.name}</h3>
                <Badge variant="outline" className="text-xs">
                  Level {station.level}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">{station.description}</p>
            </div>
          </div>
          
          <div className="flex gap-1">
            {/* Favorite button - commented out for now */}
            {/* <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onToggleFavorite(station.id)}
              title={station.is_favorite ? "Remove from favorites" : "Add to favorites"}
            >
              <Star className={`w-4 h-4 ${station.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            </Button> */}
            
            {/* Configuration buttons - Admin only */}
            {isAdminMode && (
              <Button 
                variant="ghost" 
                size="sm"
                disabled
                title="Connect External Database - coming soon"
                className="opacity-50 cursor-not-allowed"
              >
                <Database className="w-4 h-4" />
              </Button>
            )}
            {isAdminMode && (
              <div className="relative">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onConfigureLog(station)}
                  title="Configure Data Collection"
                >
                  <FileText className="w-4 h-4" />
                </Button>
                {/* Subtle Data Configuration Limit Indicator - Only for Workspace Admins */}
                {/* {dataConfigurationUsage && (
                  <div className="absolute -top-1 -right-1 text-xs text-muted-foreground bg-background px-1 rounded">
                    {dataConfigurationUsage.current}/{dataConfigurationUsage.isUnlimited ? '∞' : dataConfigurationUsage.limit}
                  </div>
                )} */}
              </div>
            )}
            
            {/* Upload button - Admin and Lead */}
            {canUploadData && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onManualUpload(station)}
                title="Manual Data Upload"
              >
                <Upload className="w-4 h-4" />
              </Button>
            )}
            
            {/* Management buttons - Admin only */}
            {isAdminMode && level < 3 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onAddSub(station.id, station.level)}
                title={`Add Level ${station.level + 1} sub-station`}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
            {isAdminMode && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onEdit(station)}
                title="Edit station"
              >
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {isAdminMode && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onRemove(station.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Data accordion */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="data-links" className="border-none">
            <AccordionTrigger className="text-sm font-medium text-muted-foreground hover:text-foreground py-2 px-0">
              View Station Data
            </AccordionTrigger>
            <AccordionContent className="pb-2">
              {dataLinks.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {dataLinks.map((link) => (
                    <Button
                      key={link.name}
                      variant="ghost"
                      size="sm"
                      className="flex items-center justify-between h-auto p-3 border border-border hover:bg-muted/50"
                      onClick={() => onViewData(station, link.name, link.configId)}
                    >
                      <div className="flex items-center gap-2">
                        <link.icon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-xs font-medium">{link.name}</span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {link.count}
                      </Badge>
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  No data collection forms configured yet.
                  {isAdminMode && (
                    <span className="block mt-1">
                      Use the "Configure Data Collection" button to set up forms.
                    </span>
                  )}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const Plan = () => {
  const [showWorkspaceSettings, setShowWorkspaceSettings] = useState(false);
  const [showProjectMembers, setShowProjectMembers] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState<any>(null);
  
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [processes, setProcesses] = useState<ManufacturingProcess[]>([]);
  const [newStationName, setNewStationName] = useState('');
  const [newStationDescription, setNewStationDescription] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [addingLevel, setAddingLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [newProductCode, setNewProductCode] = useState('');
  const [newProductDescription, setNewProductDescription] = useState('');
  const [userWorkspaceRole, setUserWorkspaceRole] = useState<string | null>(null);
  const [currentUserProjectRole, setCurrentUserProjectRole] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingStation, setEditingStation] = useState<ManufacturingProcess | null>(null);
  const [editStationName, setEditStationName] = useState('');
  const [editStationDescription, setEditStationDescription] = useState('');
  const [showLogConfigModal, setShowLogConfigModal] = useState(false);
  const [configStation, setConfigStation] = useState<ManufacturingProcess | null>(null);
  const [manualConfigs, setManualConfigs] = useState<any[]>([]);
  const [formSubmissions, setFormSubmissions] = useState<any[]>([]);
  const [sampleDataCounts, setSampleDataCounts] = useState<Record<string, number>>({});
  const [showManualUploadModal, setShowManualUploadModal] = useState(false);
  const [uploadStation, setUploadStation] = useState<ManufacturingProcess | null>(null);
  const [showFormDataViewer, setShowFormDataViewer] = useState(false);
  const [viewerStation, setViewerStation] = useState<ManufacturingProcess | null>(null);
  const [viewerFormTitle, setViewerFormTitle] = useState('');
  const [viewerConfigId, setViewerConfigId] = useState('');
  const [showSTLViewer, setShowSTLViewer] = useState(false);
  const [stlViewerStation, setSTLViewerStation] = useState<ManufacturingProcess | null>(null);
  const [stlViewerFormTitle, setSTLViewerFormTitle] = useState('');
  const [stlViewerConfigId, setSTLViewerConfigId] = useState('');
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageViewerStation, setImageViewerStation] = useState<ManufacturingProcess | null>(null);
  const [imageViewerFormTitle, setImageViewerFormTitle] = useState('');
  const [imageViewerConfigId, setImageViewerConfigId] = useState('');
  // const [dataConfigurationUsage, setDataConfigurationUsage] = useState<{
  //   current: number;
  //   limit: number;
  //   remaining: number;
  //   isUnlimited: boolean;
  // } | null>(null);
  
  // Project limits state
  const [projectLimits, setProjectLimits] = useState<{
    maxProjects: number;
    currentProjects: number;
    canCreate: boolean;
  } | null>(null);
  
  const { toast } = useToast();
  const { user, signOut, loading: authLoading, showWorkspaceModal, closeWorkspaceModal } = useAuth();
  const { tenant } = useTenant();

  // Load current workspace data for header
  useEffect(() => {
    const loadCurrentWorkspace = async () => {
      if (!user) return;

      try {
        const currentSlug = getCurrentTenantSlug();
        
        if (currentSlug && currentSlug !== 'default') {
          try {
            const workspace = await WorkspaceService.getWorkspaceBySlug(currentSlug);
            
            if (workspace) {
              setCurrentWorkspace(workspace);
              return;
            } else {
              // Fallback: Check if user has access to any workspace with this slug
              const userWorkspaces = await WorkspaceService.getUserWorkspaces();
              const matchingWorkspace = userWorkspaces.find(ws => ws.slug === currentSlug);
              
              if (matchingWorkspace) {
                setCurrentWorkspace(matchingWorkspace);
                return;
              } else {
                // console.log('❌ Plan: User does not have access to workspace:', currentSlug);
              }
            }
          } catch (workspaceError) {
            // Fallback: Get user's actual workspace instead of creating fake one
            if (user) {
              // console.log('🔧 Plan: No workspace loaded, trying to get user default workspace...');
              try {
                const defaultWorkspace = await WorkspaceService.getDefaultWorkspace();
                if (defaultWorkspace) {
                  // console.log('✅ Plan: Loaded default workspace as fallback:', defaultWorkspace);
                  setCurrentWorkspace(defaultWorkspace);
                } else {
                  // If no default, get any workspace the user has
                  const userWorkspaces = await WorkspaceService.getUserWorkspaces();
                  if (userWorkspaces.length > 0) {
                    // console.log('✅ Plan: Loaded first available workspace as fallback:', userWorkspaces[0]);
                    setCurrentWorkspace(userWorkspaces[0]);
                  } else {
                    // console.log('❌ Plan: User has no workspaces - settings will be limited');
                    setCurrentWorkspace(null);
                  }
                }
              } catch (fallbackError) {
                // console.error('❌ Plan: Failed to load fallback workspace:', fallbackError);
                setCurrentWorkspace(null);
              }
            }
          }
        }
      } catch (error) {
        // Error fallback
        if (user) {
          // console.log('🔧 Plan: Error fallback workspace');
          setCurrentWorkspace({
            id: 'error-fallback',
            name: 'Workspace',
            slug: 'default',
            domain: null,
            domain_verified: false
          });
        }
      }
    };

    loadCurrentWorkspace();
  }, [user]);

  // Fetch project limits when workspace loads
  useEffect(() => {
    if (currentWorkspace) {
      fetchProjectLimits();
    }
  }, [currentWorkspace]);

  // Determine user roles
  const isWorkspaceAdmin = userWorkspaceRole === 'workspace_admin';
  
  // Check if user has admin permissions for the current project
  const isProjectAdmin = currentUserProjectRole === 'project_admin';
  const isProjectLead = currentUserProjectRole === 'project_lead';
  
  // Determine if user can perform admin actions
  const canManageProject = isProjectAdmin || isWorkspaceAdmin; // Only Admin can manage (not Lead)
  const canManageMembers = isProjectAdmin;
  const canCreateProjects = isWorkspaceAdmin;
  const canUploadData = isProjectAdmin || isProjectLead || isWorkspaceAdmin; // Admin and Lead can upload

  useEffect(() => {
    const loadUserRole = async () => {
      try {
        const currentSlug = getCurrentTenantSlug();
        
        if (currentSlug) {
          const workspace = await WorkspaceService.getWorkspaceBySlug(currentSlug);
          const role = workspace?.role || null;
          setUserWorkspaceRole(role);
        }
      } catch (error) {
        // console.error('Error loading user workspace role:', error);
        setCurrentUserProjectRole(null);
      }
    };

    if (user) {
      loadUserRole();
    }
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      fetchProcesses(selectedProduct.id);
      fetchManualConfigs(selectedProduct.id);
      fetchFormSubmissions(selectedProduct.id);
    } else {
      setProcesses([]);
      setManualConfigs([]);
      setFormSubmissions([]);
    }
  }, [selectedProduct]);

  // Fetch sample data counts after manual configs are loaded
  useEffect(() => {
    if (selectedProduct && manualConfigs.length > 0) {
      fetchSampleDataCounts(selectedProduct.id);
    } else {
      setSampleDataCounts({});
    }
  }, [selectedProduct, manualConfigs]);

  // Fetch current user's project role when project is selected
  useEffect(() => {
    if (selectedProduct && user) {
      fetchCurrentUserProjectRole(selectedProduct.id);
    } else {
      setCurrentUserProjectRole(null);
    }
  }, [selectedProduct, user]);

  // Debug logging for manual upload modal
  useEffect(() => {
    if (showManualUploadModal) {
      // console.log('=== MANUAL DATA UPLOAD MODAL RENDER DEBUG ===');
      // console.log('Modal isOpen:', showManualUploadModal);
      // console.log('Selected product:', selectedProduct?.name, selectedProduct?.id);
      // console.log('Product ID being passed to modal:', selectedProduct?.id);
    }
  }, [showManualUploadModal, selectedProduct]);

  // Debug logging for selectedProduct changes
  useEffect(() => {
    // console.log('=== SELECTED PRODUCT STATE CHANGE ===');
    // console.log('selectedProduct changed to:', selectedProduct?.name, selectedProduct?.id);
  }, [selectedProduct]);

  const fetchCurrentUserProjectRole = async (projectId: string) => {
    try {
      const role = await ProjectMemberService.getCurrentUserProjectRole(projectId);
      setCurrentUserProjectRole(role);
    } catch (error) {
      // console.error('Error fetching current user project role:', error);
      setCurrentUserProjectRole(null);
    }
  };

  const fetchProducts = async () => {
    try {
      // Only fetch products that the user has access to based on project-level permissions
      const accessibleProjects = await ProjectService.getUserAccessibleProjects();
      
      setProducts(accessibleProjects || []);
      if (accessibleProjects && accessibleProjects.length > 0) {
        setSelectedProduct(accessibleProjects[0]);
      } else {
        setSelectedProduct(null);
      }
    } catch (error) {
      // console.error('Error fetching products:', error);
      toast({
        title: "Error",
        description: "Failed to load products. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
    
    // Refresh project limits after fetching products
    fetchProjectLimits();
  };

  const fetchProjectLimits = async () => {
    try {
      if (!currentWorkspace?.id) return;
      
      const result = await ProjectService.canCreateProject(currentWorkspace.id);
      setProjectLimits({
        maxProjects: result.maxProjects,
        currentProjects: result.currentProjects,
        canCreate: result.canCreate
      });
    } catch (error) {
      // console.error('Error fetching project limits:', error);
      setProjectLimits(null);
    }
  };

  const fetchProcesses = async (productId: string) => {
    try {
      const { data, error } = await supabase
        .from('manufacturing_processes')
        .select('*')
        .eq('product_id', productId)
        .order('sort_order');

      if (error) throw error;
      setProcesses(data || []);
    } catch (error) {
      // console.error('Error fetching processes:', error);
      toast({
        title: "Error",
        description: "Failed to load processes. Please try again.",
        variant: "destructive",
      });
    }
  };

  const fetchManualConfigs = async (productId: string) => {
    if (!user) return;
    
    // Get current workspace with fallback logic (same as main workspace loading)
    const currentSlug = getCurrentTenantSlug();
    let workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    // If workspace not found by slug, try fallback logic
    if (!workspace) {
      try {
        const userWorkspaces = await WorkspaceService.getUserWorkspaces();
        if (userWorkspaces && userWorkspaces.length > 0) {
          // Try to find default workspace, or use first one
          workspace = userWorkspaces.find(ws => ws.is_default) || userWorkspaces[0];
        }
      } catch (error) {
        // console.error('Error getting user workspaces for manual configs:', error);
      }
    }
    
    if (!workspace) return;
    
    try {
      const { data, error } = await (supabase as any)
        .from('watt_manual_config')
        .select('*')
        .eq('product_id', productId)
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      setManualConfigs(data || []);
    } catch (error) {
      // console.error('Error fetching manual configs:', error);
      // Don't show error toast for this as it's not critical
    }
  };

  const fetchFormSubmissions = async (productId: string) => {
    if (!user) return;
    
    // Get current workspace with fallback logic
    const currentSlug = getCurrentTenantSlug();
    let workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    // If workspace not found by slug, try fallback logic
    if (!workspace) {
      try {
        const userWorkspaces = await WorkspaceService.getUserWorkspaces();
        if (userWorkspaces && userWorkspaces.length > 0) {
          workspace = userWorkspaces.find(ws => ws.is_default) || userWorkspaces[0];
        }
      } catch (error) {
        // console.error('Error getting user workspaces for form submissions:', error);
      }
    }
    
    if (!workspace) return;
    
    try {
      const { data, error } = await (supabase as any)
        .from('watt_form_submissions')
        .select('*')
        .eq('product_id', productId)
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      setFormSubmissions(data || []);
    } catch (error) {
      // console.error('Error fetching form submissions:', error);
      // Don't show error toast for this as it's not critical
    }
  };

  const fetchSampleDataCounts = async (productId: string) => {
    if (!user) return;
    
    try {
      // Get all manual configs for this product that are data-type
      const dataConfigs = manualConfigs.filter(config => config.data_type === 'data');
      
      const counts: Record<string, number> = {};
      
      // For each data config, count samples from sample_data table
      for (const config of dataConfigs) {
        if (config.config_data?.part_configuration_id) {
          const { count, error } = await (supabase as any)
            .from('sample_data')
            .select('*', { count: 'exact', head: true })
            .eq('part_configuration_id', config.config_data.part_configuration_id);

          if (error) {
            // console.error(`Error counting sample data for config ${config.id}:`, error);
            counts[config.id] = 0;
          } else {
            counts[config.id] = count || 0;
          }
        } else {
          counts[config.id] = 0;
        }
      }
      
      setSampleDataCounts(counts);
    } catch (error) {
      // console.error('Error fetching sample data counts:', error);
      // Don't show error toast for this as it's not critical
    }
  };

  const addStation = async (parentId?: string, level: number = 1) => {
    if (!newStationName.trim() || !selectedProduct) return;

    // Get current workspace
    const currentSlug = getCurrentTenantSlug();
    const workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    if (!workspace) {
      toast({
        title: "Error",
        description: "Could not determine current workspace",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await (supabase as any)
        .from('manufacturing_processes')
        .insert({
          product_id: selectedProduct.id,
          name: newStationName,
          description: newStationDescription,
          process_type: 'assembly',
          duration: 20,
          workers: 1,
          level,
          parent_id: parentId || null,
          sort_order: processes.length,
          workspace_id: workspace.id
        })
        .select()
        .single();

      if (error) throw error;

      await fetchProcesses(selectedProduct.id);
      setNewStationName('');
      setNewStationDescription('');
      setShowAddForm(false);
      setSelectedParentId(null);
      setAddingLevel(1);

      toast({
        title: "Success",
        description: "Manufacturing station added successfully",
      });
    } catch (error) {
      // console.error('Error adding station:', error);
      toast({
        title: "Error",
        description: "Failed to add manufacturing station. Please try again.",
        variant: "destructive",
      });
    }
  };

  const addProduct = async () => {
    
    if (!newProductName.trim()) {
      return;
    }



    // Get current workspace
    const currentSlug = getCurrentTenantSlug();
    const workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    if (!workspace) {
      toast({
        title: "Error",
        description: "Could not determine current workspace",
        variant: "destructive",
      });
      return;
    }

    try {
      
      // Use ProjectService to create project with limit checking
      const result = await ProjectService.createProject(workspace.id, {
        name: newProductName,
        description: newProductDescription || undefined,
        code: newProductCode || undefined
      });

      if (!result.success) {
        // console.error('❌ Project creation failed:', result.error);
        toast({
          title: "Project Limit Reached",
          description: result.error || "You have reached the maximum number of projects for your plan.",
          variant: "destructive",
        });
        return;
      }
      
      // Auto-assign current user as project admin
      if (user) {
        try {
          const { error: memberError } = await (supabase as any)
            .from('project_members')
            .insert({
              project_id: result.project.id,
              user_id: user.id,
              role: 'project_admin',
              assigned_by: user.id
            });

          if (memberError) {
            // console.warn('⚠️ Could not auto-assign project admin:', memberError);
            // Don't fail the whole operation, just log the warning
          } else {
            // console.log('✅ Project admin assigned successfully');
          }
        } catch (memberError) {
          // console.warn('⚠️ Error assigning project admin:', memberError);
          // Don't fail the whole operation
        }
      }
      
      await fetchProducts();
      setSelectedProduct(result.project);
      setNewProductName('');
      setNewProductCode('');
      setNewProductDescription('');
      setShowAddProductForm(false);

      toast({
        title: "Success",
        description: "Project added successfully",
      });
    } catch (error) {
      // console.error('💥 Error adding product:', error);
      toast({
        title: "Error",
        description: "Failed to create product. Please try again.",
        variant: "destructive",
      });
    }
  };

  const addSubStation = (parentId: string, parentLevel: number) => {
    setSelectedParentId(parentId);
    setAddingLevel(parentLevel + 1);
    setShowAddForm(true);
  };

  const openEditForm = (station: ManufacturingProcess) => {
    setEditingStation(station);
    setEditStationName(station.name);
    setEditStationDescription(station.description || '');
    setShowEditForm(true);
  };

  const editStation = async () => {
    if (!editStationName.trim() || !editingStation || !selectedProduct) return;

    try {
      const { error } = await supabase
        .from('manufacturing_processes')
        .update({
          name: editStationName,
          description: editStationDescription || null,
        })
        .eq('id', editingStation.id);

      if (error) throw error;

      await fetchProcesses(selectedProduct.id);
      setEditStationName('');
      setEditStationDescription('');
      setShowEditForm(false);
      setEditingStation(null);

      toast({
        title: "Success",
        description: "Manufacturing station updated successfully",
      });
    } catch (error) {
      // console.error('Error updating station:', error);
      toast({
        title: "Error",
        description: "Failed to update station. Please try again.",
        variant: "destructive",
      });
    }
  };

  const openLogConfig = (station: ManufacturingProcess) => {
    setConfigStation(station);
    setShowLogConfigModal(true);
  };

  const handleLogConfigSave = (config: { dataType: string; formTitle: string; fields: any[] }) => {
    let description = '';
    switch (config.dataType) {
      case 'form':
        description = `Log form "${config.formTitle}" has been configured for ${configStation?.name}`;
        break;
      case 'data':
        description = `Data collection has been configured for ${configStation?.name}`;
        break;
      case 'visual-defect':
        description = `Visual defect tracking has been configured for ${configStation?.name}`;
        break;
      default:
        description = `Data collection has been configured for ${configStation?.name}`;
    }
    
    // Only show toast for non-form types (form types are handled in the modal)
    if (config.dataType !== 'form') {
      toast({
        title: "Configuration saved",
        description: description,
      });
    }

    setShowLogConfigModal(false);
    setConfigStation(null);
  };

  const handleConfigSaved = async () => {
    if (selectedProduct) {
      await fetchManualConfigs(selectedProduct.id);
      // Sample data counts will be automatically refreshed by the useEffect
      
      // Refresh data configuration usage for workspace admins
      // if (currentWorkspace?.role === 'workspace_admin') {
      //   const usage = await DataConfigurationService.getDataConfigurationUsage();
      //   // setDataConfigurationUsage(usage); // This line was commented out in the original file
      // }
    }
  };

  const refreshDataConfigurations = async () => {
    if (selectedProduct) {
      await fetchManualConfigs(selectedProduct.id);
      // Sample data counts will be automatically refreshed by the useEffect
      
      // Refresh data configuration usage for workspace admins
      // if (currentWorkspace?.role === 'workspace_admin') {
      //   const usage = await DataConfigurationService.getDataConfigurationUsage();
      //   // setDataConfigurationUsage(usage); // This line was commented out in the original file
      // }
    }
  };

  const handleSubmissionSaved = async () => {
    if (selectedProduct) {
      await fetchFormSubmissions(selectedProduct.id);
      // Also refresh sample data counts in case new sample data was uploaded
      if (manualConfigs.length > 0) {
        await fetchSampleDataCounts(selectedProduct.id);
      }
    }
  };

  const removeStation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('manufacturing_processes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await fetchProcesses(selectedProduct!.id);
      toast({
        title: "Success",
        description: "Manufacturing station removed successfully",
      });
    } catch (error) {
      // console.error('Error removing station:', error);
      toast({
        title: "Error",
        description: "Failed to remove station. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Favorite functionality commented out for now
  /*
  const toggleFavorite = async (id: string) => {
    try {
      const station = processes.find(p => p.id === id);
      if (!station) return;

      const { error } = await supabase
        .from('manufacturing_processes')
        .update({ is_favorite: !station.is_favorite })
        .eq('id', id);

      if (error) throw error;

      await fetchProcesses(selectedProduct!.id);
      toast({
        title: "Success",
        description: station.is_favorite ? "Removed from favorites" : "Added to favorites",
      });
    } catch (error) {
      // console.error('Error toggling favorite:', error);
      toast({
        title: "Error",
        description: "Failed to update favorite status. Please try again.",
        variant: "destructive",
      });
    }
  };
  */

  // Organize processes by hierarchy
  const organizeProcesses = () => {
    const rootProcesses = processes.filter(p => p.level === 1 && !p.parent_id);
    return rootProcesses.map(root => ({
      ...root,
      children: getChildren(root.id)
    }));
  };

  const getChildren = (parentId: string): ManufacturingProcess[] => {
    return processes
      .filter(p => p.parent_id === parentId)
      .map(child => ({
        ...child,
        children: getChildren(child.id)
      }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const activeProcess = processes.find(p => p.id === active.id);
    const overProcess = processes.find(p => p.id === over.id);

    if (!activeProcess || !overProcess) {
      return;
    }

    // Only allow reordering within the same level and parent
    if (activeProcess.level !== overProcess.level || activeProcess.parent_id !== overProcess.parent_id) {
      toast({
        title: "Cannot reorder",
        description: "You can only reorder stations within the same level and parent",
        variant: "destructive",
      });
      return;
    }

    // Get all processes at the same level and parent
    const sameGroupProcesses = processes.filter(p => 
      p.level === activeProcess.level && p.parent_id === activeProcess.parent_id
    );

    const oldIndex = sameGroupProcesses.findIndex(p => p.id === active.id);
    const newIndex = sameGroupProcesses.findIndex(p => p.id === over.id);

    if (oldIndex === newIndex) return;

    const reorderedProcesses = arrayMove(sameGroupProcesses, oldIndex, newIndex);

    // Update sort_order for all affected processes
    try {
      const updates = reorderedProcesses.map((process, index) => ({
        id: process.id,
        sort_order: index
      }));

      // Update the database
      for (const update of updates) {
        const { error } = await supabase
          .from('manufacturing_processes')
          .update({ sort_order: update.sort_order })
          .eq('id', update.id);

        if (error) throw error;
      }

      // Refresh the processes
      await fetchProcesses(selectedProduct!.id);
      
      toast({
        title: "Success",
        description: "Station order updated successfully",
      });
    } catch (error) {
      // console.error('Error updating station order:', error);
      toast({
        title: "Error",
        description: "Failed to update station order. Please try again.",
        variant: "destructive",
      });
    }
  };

  const totalDuration = processes.reduce((sum, process) => sum + process.duration, 0);
  const totalWorkers = processes.reduce((sum, process) => sum + process.workers, 0);

  const openManualUpload = (station: ManufacturingProcess) => {
    
    setUploadStation(station);
    setShowManualUploadModal(true);
  };

  const openFormDataViewer = async (station: ManufacturingProcess, formTitle: string, configId: string) => {
    // Find the config to check its data type
    const config = manualConfigs.find(c => c.id === configId);
    
    if (config && config.data_type === 'data') {
      // For data configurations, check if it's 2D or 3D visual
      const partConfigId = config.config_data?.part_configuration_id;
      
      if (partConfigId) {
        try {
          const { data: partConfig, error } = await (supabase as any)
            .from('part_configurations')
            .select('visual_requirement')
            .eq('id', partConfigId)
            .single();

          if (error) {
            // console.error('Error fetching part configuration:', error);
            // Default to STL viewer on error
            setSTLViewerStation(station);
            setSTLViewerFormTitle(formTitle);
            setSTLViewerConfigId(configId);
            setShowSTLViewer(true);
            return;
          }

          if (partConfig?.visual_requirement === '2d-visual') {
            // Open Image viewer for 2D visual configurations
            setImageViewerStation(station);
            setImageViewerFormTitle(formTitle);
            setImageViewerConfigId(configId);
            setShowImageViewer(true);
          } else {
            // Open STL viewer for 3D visual configurations
            setSTLViewerStation(station);
            setSTLViewerFormTitle(formTitle);
            setSTLViewerConfigId(configId);
            setShowSTLViewer(true);
          }
        } catch (error) {
          // console.error('Error checking visual requirement:', error);
          // Default to STL viewer on error
          setSTLViewerStation(station);
          setSTLViewerFormTitle(formTitle);
          setSTLViewerConfigId(configId);
          setShowSTLViewer(true);
        }
      } else {
        // No part configuration ID - default to STL viewer
        setSTLViewerStation(station);
        setSTLViewerFormTitle(formTitle);
        setSTLViewerConfigId(configId);
        setShowSTLViewer(true);
      }
    } else {
      // Open form data viewer for form configurations
      setViewerStation(station);
      setViewerFormTitle(formTitle);
      setViewerConfigId(configId);
      setShowFormDataViewer(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 flex items-center justify-center">
        <div className="text-center">
          <Factory className="w-12 h-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
      {/* Header */}
      <Header onSettingsClick={() => setShowWorkspaceSettings(true)} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
              Manufacturing Process Planner
            </h1>
            <p className="text-xl text-muted-foreground">
              Design and optimize your manufacturing workflow by mapping out process stations.
            </p>
          </div>

          {/* Product Selection */}
          <Card className="mb-6">
            <CardHeader className="relative">
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Project Selection
                </div>
                <div className="flex items-center gap-2">
                  {selectedProduct && canManageMembers && (
                    <Button 
                      onClick={() => setShowProjectMembers(true)}
                      size="sm"
                      variant="outline"
                      className="gap-2"
                    >
                      <Users className="w-4 h-4" />
                      Manage Members
                    </Button>
                  )}
                  {canCreateProjects && (
                    <Button 
                      onClick={() => setShowAddProductForm(true)}
                      size="sm"
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Project
                    </Button>
                  )}
                </div>
              </CardTitle>
              <CardDescription>
                Select a project to view and manage its manufacturing process
              </CardDescription>
              {/* Subtle Project Limit Indicator - Only for Workspace Admins */}
              {canCreateProjects && projectLimits && projectLimits.maxProjects !== -1 && (
                <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                  {projectLimits.currentProjects}/{projectLimits.maxProjects}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {/* Add Product Form */}
              {showAddProductForm && (
                <div className="mb-6 p-4 border rounded-lg bg-muted/50">
                  <div className="space-y-4">
                    <div className="text-sm text-muted-foreground mb-3">
                      Add a new project
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Input
                          placeholder="Project name (required)"
                          value={newProductName}
                          onChange={(e) => setNewProductName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Project code (optional)"
                          value={newProductCode}
                          onChange={(e) => setNewProductCode(e.target.value)}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder="Description (optional)"
                          value={newProductDescription}
                          onChange={(e) => setNewProductDescription(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={addProduct} 
                        size="sm"
                        disabled={!newProductName.trim()}
                      >
                        Add Project
                      </Button>
                      <Button 
                        onClick={() => {
                          setShowAddProductForm(false);
                          setNewProductName('');
                          setNewProductCode('');
                          setNewProductDescription('');
                        }} 
                        variant="outline" 
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-4 items-center">
                <Select
                  value={selectedProduct?.id}
                  onValueChange={(value) => {
                    const product = products.find(p => p.id === value);
                    setSelectedProduct(product || null);
                  }}
                >
                  <SelectTrigger className="flex-1 max-w-md">
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((product) => (
                      <SelectItem key={product.id} value={product.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{product.name}</span>
                          {product.code && (
                            <Badge variant="outline" className="text-xs">
                              {product.code}
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedProduct && (
                <p className="text-sm text-muted-foreground mt-2">
                  {selectedProduct.description}
                </p>
              )}
            </CardContent>
          </Card>

          {selectedProduct && (
            <>
              {/* Process Flow */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Flow - {selectedProduct.name}</span>
                    {canManageProject && (
                      <Button 
                        onClick={() => setShowAddForm(true)}
                        size="sm"
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Station
                      </Button>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Your current manufacturing process with stations and flow direction
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Add Station Form */}
                  {showAddForm && (
                    <div className="mb-6 p-4 border rounded-lg bg-muted/50">
                      <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                          {selectedParentId ? `Adding Level ${addingLevel} station` : 'Adding Level 1 station'}
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Input
                              placeholder="Enter station name"
                              value={newStationName}
                              onChange={(e) => setNewStationName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && newStationName.trim() && addStation(selectedParentId || undefined, addingLevel)}
                            />
                          </div>
                          <div>
                            <Input
                              placeholder="Enter station description (optional)"
                              value={newStationDescription}
                              onChange={(e) => setNewStationDescription(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && newStationName.trim() && addStation(selectedParentId || undefined, addingLevel)}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={() => addStation(selectedParentId || undefined, addingLevel)} 
                              size="sm"
                              disabled={!newStationName.trim()}
                            >
                              Add
                            </Button>
                            <Button 
                              onClick={() => {
                                setShowAddForm(false);
                                setNewStationName('');
                                setNewStationDescription('');
                                setSelectedParentId(null);
                                setAddingLevel(1);
                              }} 
                              variant="outline" 
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Edit Station Form */}
                  {showEditForm && editingStation && (
                    <div className="mb-6 p-4 border rounded-lg bg-muted/50">
                      <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                          Editing {editingStation.name} (Level {editingStation.level})
                        </div>
                        <div className="space-y-3">
                          <div>
                            <Input
                              placeholder="Enter station name"
                              value={editStationName}
                              onChange={(e) => setEditStationName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && editStationName.trim() && editStation()}
                            />
                          </div>
                          <div>
                            <Input
                              placeholder="Enter station description (optional)"
                              value={editStationDescription}
                              onChange={(e) => setEditStationDescription(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && editStationName.trim() && editStation()}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              onClick={editStation} 
                              size="sm"
                              disabled={!editStationName.trim()}
                            >
                              Update
                            </Button>
                            <Button 
                              onClick={() => {
                                setShowEditForm(false);
                                setEditStationName('');
                                setEditStationDescription('');
                                setEditingStation(null);
                              }} 
                              variant="outline" 
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Log Configuration Modal */}
                  <LogConfigModal
                    isOpen={showLogConfigModal}
                    onClose={() => setShowLogConfigModal(false)}
                    station={configStation}
                    onSave={handleLogConfigSave}
                    productId={selectedProduct?.id}
                    onConfigSaved={handleConfigSaved}
                    onRefreshData={refreshDataConfigurations}
                  />

                  {/* Manual Data Upload Modal */}
                  <ManualDataUploadModal
                    isOpen={showManualUploadModal}
                    onClose={() => setShowManualUploadModal(false)}
                    station={uploadStation}
                    manualConfigs={manualConfigs}
                    productId={selectedProduct?.id}
                    onSubmissionSaved={handleSubmissionSaved}
                  />

                                      {/* Form Data Viewer Modal */}
                    <FormDataViewerModal
                      isOpen={showFormDataViewer}
                      onClose={() => setShowFormDataViewer(false)}
                      station={viewerStation}
                      formTitle={viewerFormTitle}
                      configId={viewerConfigId}
                      manualConfigs={manualConfigs}
                    />

                  {/* Process Flow Visualization */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="space-y-4">
                      {/* Level 1 Stations */}
                      <SortableContext
                        items={organizeProcesses().map(p => p.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {organizeProcesses().map((process, index) => (
                          <div key={process.id}>
                            {/* Level 1 Station */}
                            <div className="flex items-center gap-4">
                              <SortableStationCard 
                                station={process} 
                                onRemove={removeStation}
                                onAddSub={addSubStation}
                                onToggleFavorite={() => {}} // Favorite functionality commented out
                                onEdit={openEditForm}
                                onConfigureLog={openLogConfig}
                                onManualUpload={openManualUpload}
                                onViewData={openFormDataViewer}
                                level={1}
                                toast={toast}
                                isAdminMode={canManageProject}
                                canUploadData={canUploadData}
                                manualConfigs={manualConfigs}
                                formSubmissions={formSubmissions}
                                sampleDataCounts={sampleDataCounts}
                                dataConfigurationUsage={null} // This line was commented out in the original file
                              />
                              {index < organizeProcesses().length - 1 && (
                                <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                              )}
                            </div>
                            
                            {/* Level 2 Stations */}
                            {process.children && process.children.length > 0 && (
                              <SortableContext
                                items={process.children.map(c => c.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                {process.children.map((child) => (
                                  <div key={child.id} className="ml-8 mt-2">
                                    <div className="flex items-center gap-4">
                                      <SortableStationCard 
                                        station={child} 
                                        onRemove={removeStation}
                                        onAddSub={addSubStation}
                                        onToggleFavorite={() => {}} // Favorite functionality commented out
                                        onEdit={openEditForm}
                                        onConfigureLog={openLogConfig}
                                        onManualUpload={openManualUpload}
                                        onViewData={openFormDataViewer}
                                        level={2}
                                        toast={toast}
                                        isAdminMode={canManageProject}
                                        canUploadData={canUploadData}
                                        manualConfigs={manualConfigs}
                                        formSubmissions={formSubmissions}
                                        sampleDataCounts={sampleDataCounts}
                                        dataConfigurationUsage={null} // This line was commented out in the original file
                                      />
                                    </div>
                                    
                                    {/* Level 3 Stations */}
                                    {child.children && child.children.length > 0 && (
                                      <SortableContext
                                        items={child.children.map(gc => gc.id)}
                                        strategy={verticalListSortingStrategy}
                                      >
                                        {child.children.map((grandchild) => (
                                          <div key={grandchild.id} className="ml-8 mt-2">
                                            <SortableStationCard 
                                              station={grandchild} 
                                              onRemove={removeStation}
                                              onAddSub={addSubStation}
                                              onToggleFavorite={() => {}} // Favorite functionality commented out
                                              onEdit={openEditForm}
                                              onConfigureLog={openLogConfig}
                                              onManualUpload={openManualUpload}
                                              onViewData={openFormDataViewer}
                                              level={3}
                                              toast={toast}
                                              isAdminMode={canManageProject}
                                              canUploadData={canUploadData}
                                              manualConfigs={manualConfigs}
                                              formSubmissions={formSubmissions}
                                              sampleDataCounts={sampleDataCounts}
                                              dataConfigurationUsage={null} // This line was commented out in the original file
                                            />
                                          </div>
                                        ))}
                                      </SortableContext>
                                    )}
                                  </div>
                                ))}
                              </SortableContext>
                            )}
                          </div>
                        ))}
                      </SortableContext>

                      {processes.length === 0 && (
                        <div className="text-center py-12">
                          <Factory className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-semibold mb-2">No stations added yet</h3>
                          <p className="text-muted-foreground mb-4">
                            Start building your manufacturing process for {selectedProduct.name} by adding your first station.
                          </p>
                          {canManageProject && (
                            <Button onClick={() => setShowAddForm(true)} className="gap-2">
                              <Plus className="w-4 h-4" />
                              Add First Station
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </DndContext>
                </CardContent>
              </Card>

              {/* Process Overview Cards - COMMENTED OUT */}
              {/* <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Clock className="w-4 h-4" />
                      Total Cycle Time
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary">{totalDuration} min</div>
                    <p className="text-sm text-muted-foreground">Estimated completion time</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Users className="w-4 h-4" />
                      Workforce Required
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary">{totalWorkers} people</div>
                    <p className="text-sm text-muted-foreground">Total workers needed</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Factory className="w-4 h-4" />
                      Process Stations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-primary">{processes.length}</div>
                    <p className="text-sm text-muted-foreground">Manufacturing steps</p>
                  </CardContent>
                </Card>
              </div> */}

              {/* Favorite Stations - commented out for now */}
              {/* {processes.filter(p => p.is_favorite).length > 0 && (
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      Favorite Stations
                    </CardTitle>
                    <CardDescription>
                      Your starred stations for quick access
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {processes.filter(p => p.is_favorite).map((station) => {
                        const stationIconMap = {
                          assembly: Factory,
                          quality: Settings,
                          packaging: Package,
                          inspection: AlertTriangle,
                          testing: Settings
                        };
                        const StationIcon = stationIconMap[station.process_type as keyof typeof stationIconMap] || Factory;

                        return (
                          <div 
                            key={station.id} 
                            className="flex items-center justify-between p-3 border rounded-lg bg-muted/20 hover:bg-muted/40 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                                <StationIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium">{station.name}</h4>
                                  <Badge variant="outline" className="text-xs">
                                    Level {station.level}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">{station.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{station.duration} min</span>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {}} // Favorite functionality commented out
                                title="Remove from favorites"
                              >
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )} */}

            </>
          )}

          {!selectedProduct && products.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No products found</h3>
                <p className="text-muted-foreground">
                  Create your first product to start planning manufacturing processes.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Modals */}

      {/* Other Modals */}
      {configStation && (
        <LogConfigModal
          isOpen={showLogConfigModal}
          onClose={() => setShowLogConfigModal(false)}
          onSave={handleLogConfigSave}
          station={configStation}
        />
      )}

      {uploadStation && (
        <ManualDataUploadModal
          isOpen={showManualUploadModal}
          onClose={() => setShowManualUploadModal(false)}
          onSubmissionSaved={handleSubmissionSaved}
          station={uploadStation}
          manualConfigs={manualConfigs}
          productId={selectedProduct?.id}
        />
      )}

      {viewerStation && (
        <FormDataViewerModal
          isOpen={showFormDataViewer}
          onClose={() => setShowFormDataViewer(false)}
          formTitle={viewerFormTitle}
          configId={viewerConfigId}
          station={viewerStation}
          manualConfigs={manualConfigs}
        />
      )}

      {stlViewerStation && (
        <STLViewerModal
          isOpen={showSTLViewer}
          onClose={() => setShowSTLViewer(false)}
          formTitle={stlViewerFormTitle}
          configId={stlViewerConfigId}
          station={stlViewerStation}
          manualConfigs={manualConfigs}
        />
      )}

      {imageViewerStation && (
        <ImageViewerModal
          isOpen={showImageViewer}
          onClose={() => setShowImageViewer(false)}
          formTitle={imageViewerFormTitle}
          configId={imageViewerConfigId}
          station={imageViewerStation}
          manualConfigs={manualConfigs}
        />
      )}

      {/* Workspace Settings Modal */}
      {currentWorkspace && (
        <WorkspaceSettingsModal
          isOpen={showWorkspaceSettings}
          onClose={() => setShowWorkspaceSettings(false)}
          workspace={currentWorkspace}
        />
      )}

      {/* Project Members Modal */}
      {selectedProduct && (
        <ProjectMembersModal
          isOpen={showProjectMembers}
          onClose={() => setShowProjectMembers(false)}
          projectId={selectedProduct.id}
          projectName={selectedProduct.name}
        />
      )}

      {/* Workspace Creation Modal */}
      <WorkspaceCreationModal
        isOpen={showWorkspaceModal}
        onClose={closeWorkspaceModal}
      />
    </div>
  );
};

export default Plan;